/**
 * InvoiceCreateModal - Modal for creating new invoices
 */

import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Plus, UserPlus } from "phosphor-react";
import { format, addDays } from "date-fns";
import LineItemsEditor from "../LineItemsEditor";
import TotalsSummary from "../TotalsSummary";
import CustomerCreateModal from "../CustomerCreateModal";
import SearchableCustomerSelect from "../SearchableCustomerSelect";

// Payment terms configuration
const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on Receipt", days: 0 },
  { value: "net_10", label: "Net 10", days: 10 },
  { value: "net_15", label: "Net 15", days: 15 },
  { value: "net_30", label: "Net 30", days: 30 },
  { value: "net_45", label: "Net 45", days: 45 },
];

export default function InvoiceCreateModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  customers,
  taxRatePercent,
  onSubmit,
  onUpdateLineItem,
  onAddLineItem,
  onRemoveLineItem,
  onCatalogSelect,
  onFormatPrice,
  onReset,
  onCustomerCreated,
  validationErrors = [],
}) {
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);

  // Initialize payment_terms if not set
  const paymentTerms = formData.payment_terms || "due_on_receipt";

  // Compute due date based on payment terms
  const computedDueDate = useMemo(() => {
    const term = PAYMENT_TERMS.find(t => t.value === paymentTerms);
    const days = term ? term.days : 0;
    return addDays(new Date(), days);
  }, [paymentTerms]);

  const handlePaymentTermsChange = (value) => {
    setFormData({ ...formData, payment_terms: value });
  };

  const handleCustomerCreated = (customer) => {
    if (onCustomerCreated) {
      onCustomerCreated(customer);
    }
    setFormData({ ...formData, customer_id: customer.customer_id });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            data-testid="add-invoice-btn"
          >
            <Plus size={18} className="mr-2" />
            New Invoice
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl" data-testid="invoice-create-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Create Invoice
            </DialogTitle>
          </DialogHeader>
          
          <DialogBody>
            <form id="invoice-create-form" onSubmit={onSubmit} className="space-y-4">
              {/* Top row: Customer + Title - responsive layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between h-5 mb-1">
                    <span className="text-sm font-medium">Customer *</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-5 px-2 text-xs text-[#FF6600] hover:text-[#CC5200] hover:bg-orange-50"
                      onClick={() => setShowQuickCustomer(true)}
                      data-testid="invoice-new-customer-btn"
                    >
                      <UserPlus size={12} className="mr-1" />
                      New
                    </Button>
                  </div>
                  <SearchableCustomerSelect
                    customers={customers}
                    value={formData.customer_id}
                    onChange={(v) => setFormData({ ...formData, customer_id: v })}
                    label=""
                    data-testid="invoice-customer-select"
                  />
                </div>
                <div>
                  <div className="h-5 mb-1">
                    <Label className="text-sm font-medium">Title *</Label>
                  </div>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Kitchen renovation invoice"
                    className="h-12 border-2"
                    required
                    data-testid="invoice-title-input"
                  />
                </div>
              </div>

              {/* Payment Terms + Due Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Payment Terms</Label>
                  <Select
                    value={paymentTerms}
                    onValueChange={handlePaymentTermsChange}
                  >
                    <SelectTrigger className="mt-1 h-12 border-2" data-testid="invoice-payment-terms-select">
                      <SelectValue placeholder="Select terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((term) => (
                        <SelectItem key={term.value} value={term.value}>
                          {term.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Due Date</Label>
                  <Input
                    value={format(computedDueDate, "MMM d, yyyy")}
                    disabled
                    className="mt-1 h-12 border-2 bg-zinc-50 text-zinc-600"
                    data-testid="invoice-due-date-display"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Auto-calculated from payment terms</p>
                </div>
              </div>

              <LineItemsEditor
                items={formData.items}
                onUpdateItem={onUpdateLineItem}
                onAddItem={onAddLineItem}
                onRemoveItem={onRemoveLineItem}
                onCatalogSelect={onCatalogSelect}
                onFormatPrice={onFormatPrice}
                taxRatePercent={taxRatePercent}
                testIdPrefix="invoice-line"
                validationErrors={validationErrors}
              />

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Payment terms or additional notes..."
                  className="mt-1 border-2"
                  rows={3}
                />
              </div>

              <TotalsSummary items={formData.items} taxRatePercent={taxRatePercent} />
            </form>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onReset();
              }}
              className="flex-1 sm:flex-none border-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="invoice-create-form"
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              disabled={!formData.customer_id || !formData.title}
              data-testid="save-invoice-btn"
            >
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Create Modal */}
      <CustomerCreateModal
        isOpen={showQuickCustomer}
        onClose={() => setShowQuickCustomer(false)}
        onCustomerCreated={handleCustomerCreated}
      />
    </>
  );
}
